import {MediaMatcher} from '@angular/cdk/layout';
import {Component, Inject, OnInit} from '@angular/core';
import {AngularFirestore, AngularFirestoreDocument} from '@angular/fire/compat/firestore';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef} from '@angular/material/legacy-dialog';
import {MatBottomSheet} from '@angular/material/bottom-sheet';
import {Subject} from 'rxjs';
import {take, takeUntil} from 'rxjs/operators';
import {TreeNode} from 'primeng/api';
import {AventiObject} from 'src/app/models/AventiObject';
import {Objecttype} from 'src/app/models/Objecttype';
import {ConfirmComponent} from 'src/app/components/common/bottom-sheet/confirm/confirm.component';
import {buildObjecttypeTree} from 'src/app/utils/objecttype-tree';
import {AdminService} from 'src/app/services/admin.service';
import {IdService} from 'src/app/services/id.service';
import {ObjectService} from 'src/app/services/object.service';
import {SharedService} from 'src/app/services/shared.service';
import {environment} from 'src/environments/environment';

export interface DialogData {
  projectid: string;
  hideOpenAfterCreated: boolean;
}

@Component({
  selector: 'app-new-object',
  templateUrl: './new-object.component.html',
  styleUrls: ['./new-object.component.css'],
  providers: [ObjectService],
})
export class NewObjectComponent implements OnInit {
  private ngUnsubscribe = new Subject();

  Form = new UntypedFormGroup({
    // no: new FormControl('', [Validators.required]),
    name: new UntypedFormControl('', [Validators.required]),
    tfmTag: new UntypedFormControl('', []),
    rdsTag: new UntypedFormControl('', []),
    desc: new UntypedFormControl('', []),
    type: new UntypedFormControl(null, [Validators.required]),
    process: new UntypedFormControl('', []),
    profilenumber: new UntypedFormControl('', []),
  });
  error: string;

  message = 'Laster opp...';
  showMessage = false;

  get no() {
    return this.Form.get('no');
  }
  get name() {
    return this.Form.get('name');
  }
  get tfmTag() {
    return this.Form.get('tfmTag');
  }
  get rdsTag() {
    return this.Form.get('rdsTag');
  }
  get desc() {
    return this.Form.get('desc');
  }
  get type() {
    return this.Form.get('type');
  }
  get process() {
    return this.Form.get('process');
  }
  get profilenumber() {
    return this.Form.get('profilenumber');
  }

  objectDoc: AngularFirestoreDocument<AventiObject>;
  objectData: AventiObject;

  objecttypes: Objecttype[];
  objecttypeTree: TreeNode[] = [];
  // The app raises .cdk-overlay-container (MatDialog) to z-index 9998, so the
  // PrimeNG panel must sit above that or it renders behind the dialog.
  treeSelectOverlayOptions = {autoZIndex: true, baseZIndex: 10000};

  mobileQuery: MediaQueryList;

  openAfterCreate = true;

  hideOpenAfterCreated = false;

  constructor(
    private afs: AngularFirestore,
    private dialogRef: MatDialogRef<NewObjectComponent>,
    private objectservice: ObjectService,
    private sharedservice: SharedService,
    private adminservice: AdminService,
    private bottomSheet: MatBottomSheet,
    media: MediaMatcher,
    private idservice: IdService,
    @Inject(MAT_DIALOG_DATA) public data: DialogData
  ) {
    // projectservice.readProject().pipe(takeUntil(this.ngUnsubscribe)).subscribe((project) => {
    //   this.projectversion = project.objecttypeversion;
    // adminservice.getObjecttypes()
    // });
    this.mobileQuery = media.matchMedia(environment.mobileQueryMaxwidthSmall);
  }

  ngOnInit(): void {
    if (this.data?.hideOpenAfterCreated) {
      this.hideOpenAfterCreated = this.data.hideOpenAfterCreated;
    }
    if (this.hideOpenAfterCreated) {
      this.openAfterCreate = false;
    }

    // Take over closing so we can guard unsaved changes on backdrop click / ESC.
    this.dialogRef.disableClose = true;
    this.dialogRef
      .backdropClick()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => this.attemptClose());
    this.dialogRef
      .keydownEvents()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((event) => {
        if (event.key === 'Escape') {
          this.attemptClose();
        }
      });

    // Subscribe to the live stream so any emission (now or later) rebuilds the tree
    this.sharedservice.objecttypes
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((types) => {
        if (Array.isArray(types) && types.length > 0) {
          this.setObjecttypes(types);
        }
      });

    // If nothing is cached yet, load once and push into the shared stream
    if (!(this.sharedservice.objecttypes.value || []).length) {
      this.adminservice
        .getAllObjecttypesObs()
        .pipe(take(1), takeUntil(this.ngUnsubscribe))
        .subscribe((types) => {
          this.sharedservice.objecttypes.next(types);
        });
    }
  }

  // Keep only active types and drop the Mottak category, then build the picker tree
  private setObjecttypes(types: Objecttype[]) {
    this.objecttypes = (types || []).filter(
      (type) => type.category?.id !== 'loJgvvKxjzx1jk771Wuf' && type.active !== false
    );
    this.objecttypeTree = buildObjecttypeTree(this.objecttypes);
  }

  selectedObjecttypeId(): string {
    const selected = this.type.value as TreeNode | null;
    return selected?.data || selected?.key || '';
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next(null);
    this.ngUnsubscribe.complete();
  }

  async create(
    name: string,
    tfmTag: string,
    rdsTag: string,
    typeid: string,
    desc?: string,
    process?: string,
    profilenumber?: string
  ) {
    let objecttypename: string;
    let projectid: string;
    if (this.data?.projectid) {
      projectid = this.data.projectid;
    } else {
      projectid = this.idservice.projectId();
    }
    this.objecttypes.forEach((type) => {
      if (type.id === typeid) {
        objecttypename = type.name;
      }
    });
    this.objectData = {
      name,
      tfmTag,
      rdsTag,
      desc,
      process,
      profilenumber,
      type: {
        id: typeid,
        name: objecttypename,
      },
      projectid,
    };
    if (this.openAfterCreate) {
      this.sharedservice.isLoading.next([
        true,
        'Oppretter objekt',
        [this.objectData.name, this.objectData.type?.name],
      ]);
    }
    this.objectData.id = this.afs.createId();
    await this.objectservice.set(this.objectData, this.openAfterCreate, true);
    this.Form.reset();
    this.Form.markAsPristine();
    this.Form.markAsUntouched();
    this.dialogRef.close({data: this.objectData});
  }

  // Close request from the Cancel button, backdrop click or ESC. Guard unsaved input.
  attemptClose() {
    if (this.Form.dirty) {
      this.confirmDiscard();
    } else {
      this.dialogRef.close();
    }
  }

  private confirmDiscard() {
    const sheet = this.bottomSheet.open(ConfirmComponent, {
      backdropClass: 'blur-dialog-backdrop',
      data: {
        title: 'Forkaste endringer?',
        text: 'Du har ulagrede endringer. Vil du lukke uten å lagre?',
        yes: {name: 'Forkast', icon: 'delete'},
        no: {name: 'Avbryt', icon: 'close'},
      },
    });
    sheet
      .afterDismissed()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((confirm) => {
        if (confirm) {
          this.dialogRef.close();
        }
      });
  }

  toggleOpenAfterCreate() {
    this.openAfterCreate = !this.openAfterCreate;
  }

  // fileChange(event): void {
  //   const fileList: FileList = event.target.files;
  //   if (fileList.length > 0) {
  //     const file = fileList[0];
  //     this.showMessage = true;
  //     this.xlservice.uploadFile(file).then(() => {
  //       this.message = 'Ferdig lastet opp';
  //     });
  //   }
  // }
}
